'use client';

import { useMemo, useState } from 'react';
import NavIntegracao from './Nav';
import AppsIntegracao from './AppsIntegracao';
import { type AppCardProps } from './Types';
import { resolveIntegracaoCards } from './IntegracaoPresets';
import { useTranslation } from '../../lib/i18n/TranslationContext';

type IntegracoesProps = {
	integracoesInfo: AppCardProps[];
	categoryDescriptions?: Record<string, string>;
};

const Integracoes: React.FC<IntegracoesProps> = ({ integracoesInfo, categoryDescriptions }) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [installStatus, setInstallStatus] = useState('all');
	const [category, setCategory] = useState('*');
	const { t } = useTranslation();

	const resolvedIntegracoes = useMemo(() => resolveIntegracaoCards(integracoesInfo), [integracoesInfo]);

	const filteredIntegracoes = useMemo(() => {
		let filtered = resolvedIntegracoes;

		if (searchTerm.trim() !== '') {
			const term = searchTerm.toLowerCase();
			filtered = filtered.filter((integration) => {
				const matchesTag = Array.isArray(integration.tags)
					? integration.tags.some((tag) => tag?.toLowerCase().includes(term))
					: false;
				const matchesTitle = (integration.title ?? '').toLowerCase().includes(term);
				const matchesDescription = integration.description
					? integration.description.toLowerCase().includes(term)
					: false;
				return matchesTag || matchesTitle || matchesDescription;
			});
		}

		if (installStatus === 'empreender') {
			filtered = filtered.filter((integration) => integration.appEmpreender);
		} else if (installStatus === 'installed') {
			filtered = filtered.filter((integration) => integration.installed);
		} else if (installStatus === 'not-installed') {
			filtered = filtered.filter((integration) => !integration.installed);
		}

		if (category !== '*') {
			filtered = filtered.filter(
				(integration) => Array.isArray(integration.tags) && integration.tags.includes(category),
			);
		}

		return filtered;
	}, [searchTerm, installStatus, category, resolvedIntegracoes]);

	return (
		<div className='custom-scroll size-full overflow-y-auto bg-white'>
			<div className='mx-auto w-full max-w-7xl px-6 pb-16 pt-10 lg:px-8'>
				<div className='flex w-full flex-col gap-1'>
					<h1 className='text-3xl font-semibold leading-9 tracking-tight text-gray-900'>
						{t('Integrações e aplicativos conectados')}
					</h1>
					<p className='text-sm font-normal leading-5 text-gray-500'>
						{t('Potencialize seu fluxo de trabalho e conecte a ferramenta que você usa todos os dias.')}
					</p>
				</div>

				<div className='mt-8 flex flex-col items-start gap-8 md:mt-10 md:flex-row md:gap-10'>
					<NavIntegracao
						integracoesInfo={resolvedIntegracoes}
						onInputChange={setSearchTerm}
						onInstallChange={setInstallStatus}
						onValueChange={setCategory}
					/>

					<AppsIntegracao
						integracoesInfo={filteredIntegracoes}
						category={category}
						grouped={category === '*' && searchTerm.trim() === '' && installStatus === 'all'}
						categoryDescriptions={categoryDescriptions}
					/>
				</div>
			</div>
		</div>
	);
};

export default Integracoes;
